import { LocalNotifications } from '@capacitor/local-notifications';
import { Geolocation } from '@capacitor/geolocation';
import { registerPlugin, Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import Swal from 'sweetalert2';

// ─── Plugin registrations ─────────────────────────────────────────────────────

// BackgroundGeolocation – used for live location tracking once Always Allow is confirmed
const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');

// LocationPermissionChecker – our native plugin (defined in AppDelegate.swift).
//   Android: checks ACCESS_BACKGROUND_LOCATION
//   iOS:     checks CLLocationManager authorizationStatus === .authorizedAlways
const LocationPermissionChecker = registerPlugin('LocationPermissionChecker');

// ─── Module-level state ───────────────────────────────────────────────────────

let initialized = false;       // startBackgroundLocationTracking() called once only
let watcherId = null;          // Active BackgroundGeolocation watcher ID
let alertOpen = false;         // Prevents duplicate Swal popups
let isAppInForeground = true;  // Tracks foreground/background state

// ─── App foreground/background listener ───────────────────────────────────────

App.addListener('appStateChange', ({ isActive }) => {
    isAppInForeground = isActive;
    if (isActive && initialized) {
        // ✅ Requirement 3 & 4: Re-check the moment user returns from Settings
        enforceAlwaysPermission();
    }
});

// ─── Native permission check ──────────────────────────────────────────────────

/**
 * Returns true ONLY when "Always Allow" is granted.
 * Uses our native plugin on both Android and iOS.
 * Falls back to BackgroundGeolocation probe if plugin is unavailable.
 */
const isAlwaysAllowed = async () => {
    try {
        const result = await LocationPermissionChecker.checkBackgroundPermission();
        console.log('[Perm] Native check result:', JSON.stringify(result));
        return result?.granted === true;
    } catch (err) {
        // Plugin not available (e.g., Xcode build hasn't run yet in dev)
        console.warn('[Perm] Native plugin unavailable:', err?.code);
        // Fall back: use Geolocation.checkPermissions() – this only catches denied/prompt,
        // not the Always vs While-Using distinction on iOS.
        // The BackgroundGeolocation watcher (started below) will handle the iOS distinction.
        return null; // null = "unknown, use watcher probe"
    }
};

// ─── Core enforcement ─────────────────────────────────────────────────────────

/**
 * Main enforcement function. Called:
 *   1. On app launch
 *   2. Every 3 seconds (interval)
 *   3. Every time app returns from background (appStateChange)
 *
 * Flow:
 *   Always Allow → close modal if open, start/keep tracking watcher
 *   Anything else → show modal, stop tracking watcher
 */
const enforceAlwaysPermission = async () => {
    const platform = Capacitor.getPlatform();

    // On iOS: ensure system permission dialog has been shown at least once
    if (platform === 'ios') {
        try {
            const s = await Geolocation.checkPermissions();
            if (s.location === 'prompt' || s.location === 'prompt-with-rationale') {
                await Geolocation.requestPermissions();
                // After dialog, re-check fires via appStateChange or interval
                return;
            }
        } catch (_) {}
    }

    const granted = await isAlwaysAllowed();

    if (granted === true) {
        // ✅ "Always Allow" is set
        if (alertOpen) {
            alertOpen = false;
            Swal.close();
        }
        if (!watcherId) {
            startTrackingWatcher();
        }
    } else if (granted === false) {
        // ❌ Not "Always Allow" (While Using / Ask Next Time / Denied)
        stopTrackingWatcher();
        showPermissionModal();
    } else {
        // null = native plugin unavailable; use the BackgroundGeolocation watcher
        // as the enforcement mechanism (it fires NOT_AUTHORIZED for non-Always on iOS)
        if (!watcherId) {
            startTrackingWatcher();
        }
    }
};

// ─── Background Geolocation watcher ──────────────────────────────────────────

const startTrackingWatcher = () => {
    if (watcherId) return;

    BackgroundGeolocation.addWatcher(
        {
            backgroundMessage: 'Hari Om PMS is tracking your active work shift and site travel in the background.',
            backgroundTitle: 'Field Duty Active - Hari Om PMS',
            requestPermissions: false,
            stale: false,
            distanceFilter: 0
        },
        (location, error) => {
            if (error) {
                // NOT_AUTHORIZED fires for "While Using" on iOS and no background perm on Android
                if (error.code === 'NOT_AUTHORIZED') {
                    console.warn('[Perm] BackgroundGeolocation NOT_AUTHORIZED – not Always Allow');
                    watcherId = null;
                    showPermissionModal();
                }
                return;
            }

            // Location received → Always Allow is active
            if (alertOpen) {
                alertOpen = false;
                Swal.close();
            }

            // Send location ping every 5 minutes
            const now = Date.now();
            const lastPing = parseInt(localStorage.getItem('last_bg_ping') || '0', 10);
            if (now - lastPing >= 300000) {
                localStorage.setItem('last_bg_ping', now.toString());
                let user_id = null;
                try { user_id = JSON.parse(localStorage.getItem('user') || '{}').id; } catch (_) {}
                if (user_id) {
                    import('../api/axios').then(({ default: api }) => {
                        api.post('/real-time-location', {
                            user_id,
                            lat: location.latitude,
                            lng: location.longitude,
                            state: isAppInForeground ? 'foreground' : 'background'
                        }, { skipLoader: true })
                            .then(r => console.log('[Location] Ping sent:', r.status))
                            .catch(e => console.error('[Location] Ping error:', e.message));
                    });
                }
            }
        }
    ).then(id => {
        watcherId = id;
        console.log('[Location] Tracking watcher started:', id);
    }).catch(err => {
        console.error('[Location] Watcher start failed:', err);
        showPermissionModal();
    });
};

const stopTrackingWatcher = () => {
    if (!watcherId) return;
    BackgroundGeolocation.removeWatcher({ id: watcherId }).catch(() => {});
    watcherId = null;
};

// ─── Entry point (called once from App.js / main component) ──────────────────

export const startBackgroundLocationTracking = async () => {
    const platform = Capacitor.getPlatform();
    if (platform !== 'android' && platform !== 'ios') return;
    if (initialized) return;
    initialized = true;

    // Request notification permissions (non-blocking, best-effort)
    try {
        const notifStatus = await LocalNotifications.checkPermissions();
        if (notifStatus.display !== 'granted') {
            await LocalNotifications.requestPermissions();
        }
    } catch (_) {}

    // ✅ Requirement 1: Check on every app launch immediately
    await enforceAlwaysPermission();

    // ✅ Requirement 3: Continuously detect if user changes permission mid-session
    setInterval(enforceAlwaysPermission, 3000);
};

// ─── Permission modal (Prominent Disclosure complying with Google Play) ─────────

const showPermissionModal = () => {
    if (alertOpen) return; // Already showing
    alertOpen = true;

    Swal.fire({
        title: 'Background Location Disclosure',
        html: `
          <style>
            .perm-container {
              padding-top: max(20px, env(safe-area-inset-top, 20px)) !important;
              padding-bottom: max(20px, env(safe-area-inset-bottom, 20px)) !important;
              padding-left: max(16px, env(safe-area-inset-left, 16px)) !important;
              padding-right: max(16px, env(safe-area-inset-right, 16px)) !important;
              box-sizing: border-box !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            .perm-popup {
              border-radius: 20px !important;
              padding: 22px 20px 18px !important;
              font-family: 'Outfit', 'Inter', sans-serif !important;
              max-width: 440px !important;
              width: calc(100% - 24px) !important;
              max-height: calc(100dvh - env(safe-area-inset-top, 24px) - env(safe-area-inset-bottom, 24px) - 32px) !important;
              overflow-y: auto !important;
              -webkit-overflow-scrolling: touch !important;
              box-shadow: 0 24px 40px rgba(0,0,0,0.18) !important;
              text-align: left !important;
              box-sizing: border-box !important;
              margin: auto !important;
            }
            .perm-badge {
              display: inline-flex; align-items: center; gap: 6px;
              background: #fff7ed; color: #ea580c; border: 1px solid #fed7aa;
              font-size: 11px; font-weight: 700; text-transform: uppercase;
              letter-spacing: 0.05em; padding: 4px 10px; border-radius: 20px;
              margin-bottom: 12px;
            }
            .perm-text {
              color: #334155; font-size: 13.5px; line-height: 1.65; margin: 0 0 14px;
            }
            .perm-text strong {
              color: #0f172a;
            }
            .perm-highlight {
              background: #f8fafc; border-left: 4px solid #ea580c;
              border-radius: 0 8px 8px 0; padding: 10px 12px;
              font-size: 12.5px; color: #475569; line-height: 1.55; margin-bottom: 14px;
            }
            .perm-steps {
              background: #fff8f1; border-radius: 10px; padding: 12px 14px;
              margin-bottom: 14px; border: 1px solid #fed7aa;
            }
            .perm-steps strong {
              font-size: 12.5px; color: #92400e; display: block; margin-bottom: 4px;
            }
            .perm-steps ol {
              margin: 0; padding-left: 18px; color: #92400e; font-size: 12.5px; line-height: 1.7;
            }
            .perm-privacy-link {
              display: block; text-align: center; font-size: 12px; color: #64748b;
              margin-top: 10px;
            }
            .perm-privacy-link a {
              color: #ea580c; text-decoration: underline; font-weight: 600;
            }
            .perm-actions {
              display: flex !important; gap: 10px !important;
              margin-top: 16px !important; width: 100% !important; padding: 0 !important;
            }
            .perm-btn-settings {
              background: linear-gradient(135deg,#f97316,#ea580c) !important;
              color: #fff !important; border: none !important;
              border-radius: 10px !important; padding: 11px 18px !important;
              font-size: 13.5px !important; font-weight: 600 !important; flex: 1 !important;
              cursor: pointer !important; display: flex !important;
              align-items: center !important; justify-content: center !important; gap: 6px !important;
            }
            .perm-btn-settings:hover { opacity: 0.9 !important; }
            .perm-btn-exit {
              background: #fff !important; color: #475569 !important;
              border: 1.5px solid #e2e8f0 !important; border-radius: 10px !important;
              padding: 11px 18px !important; font-size: 13.5px !important;
              font-weight: 600 !important; flex: 1 !important; cursor: pointer !important;
              display: flex !important; align-items: center !important;
              justify-content: center !important; gap: 6px !important;
            }
            .perm-btn-exit:hover { background: #f8fafc !important; }
          </style>

          <div class="perm-badge">📍 Prominent Disclosure</div>

          <p class="perm-text">
            <strong>Hari Om PMS</strong> collects location data to enable <strong>Real-Time Field Technician Attendance & Site Visit Tracking</strong> even when the app is closed or not in use (in the background).
          </p>

          <div class="perm-highlight">
            🛡️ <strong>Why this is required:</strong> To accurately log on-duty shift hours, calculate inter-site travel distance, and coordinate electrical field engineers while travelling between customer project sites. Location data is collected <em>only during active duty</em> and is never sold or used for ads.
          </div>

          <div class="perm-steps">
            <strong>⚙️ How to grant "Allow all the time":</strong>
            <ol>
              <li>Tap <strong>Grant Permission / Settings</strong> below</li>
              <li>Tap <strong>Permissions</strong> &rarr; <strong>Location</strong></li>
              <li>Select <strong>Allow all the time</strong></li>
              <li>Return to <strong>Hari Om PMS</strong></li>
            </ol>
          </div>

          <div class="perm-privacy-link">
            Learn more in our <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </div>`,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Grant Permission',
        cancelButtonText: 'Not Now / Exit',
        buttonsStyling: false,
        customClass: {
            container: 'perm-container',
            popup: 'perm-popup',
            actions: 'perm-actions',
            confirmButton: 'perm-btn-settings',
            cancelButton: 'perm-btn-exit',
        },
        allowOutsideClick: false,
        allowEscapeKey: false
    }).then((result) => {
        alertOpen = false;
        if (!Capacitor.isNativePlatform()) return;
        if (result.isConfirmed) {
            // ✅ Requirement 4: On return, appStateChange fires enforceAlwaysPermission()
            NativeSettings.open({
                optionAndroid: AndroidSettings.ApplicationDetails,
                optionIOS: IOSSettings.App
            });
        } else if (result.dismiss === Swal.DismissReason.cancel) {
            App.exitApp();
        }
    });
};
